import { useEffect, useState, useRef } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import DeadlineCalendar from "@/components/DeadlineCalendar";
import PdfThumbnail from "@/components/PdfThumbnail";
import {
  LayoutDashboard, Wrench, FileText, CalendarDays, FolderOpen, Map as MapIcon,
  MessageSquare, UserCircle, LogOut, Upload, Download, Trash2, Plus, ChevronRight,
  Copy as CopyIcon, Mail,
} from "lucide-react";

const NAV = [
  { to: "/dashboard",            label: "Dashboard",      icon: LayoutDashboard, end: true },
  { to: "/dashboard/tools",      label: "My Tools",       icon: Wrench },
  { to: "/dashboard/transcripts",label: "Transcripts",    icon: FileText },
  { to: "/dashboard/deadlines",  label: "Deadlines",      icon: CalendarDays },
  { to: "/dashboard/vault",      label: "Document Vault", icon: FolderOpen },
  { to: "/dashboard/roadmap",    label: "Roadmap",        icon: MapIcon },
  { to: "/dashboard/advisor",    label: "Advisor",        icon: MessageSquare, paidOnly: true },
  { to: "/dashboard/account",    label: "Account",        icon: UserCircle },
];

const PAID = new Set(["platform", "florida_transfer", "selective_elite", "state", "nationwide"]);

function StatusDot({ level }) {
  const color = level === "risk" ? "bg-brass" : level === "warn" ? "bg-brass-soft" : "bg-ivy";
  return <span className={`inline-block h-2 w-2 ${color}`} />;
}

export function usePortalMe() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const refresh = async () => {
    try { const r = await api.get("/portal/me"); setMe(r.data); } catch { setMe(null); } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);
  return { me, loading, refresh };
}

export default function PortalShell() {
  const { user, loading: authLoading, logout } = useAuth();
  const { me, loading: meLoading } = usePortalMe();
  const navigate = useNavigate();
  const location = useLocation();

  if (authLoading || meLoading) return <div className="min-h-screen bg-bone flex items-center justify-center text-ink-soft">Loading…</div>;
  if (!user) { navigate(`/login?return=${encodeURIComponent(location.pathname)}`, { replace: true }); return null; }

  const isPaid = PAID.has(me?.tier);
  const isAuditOnly = !me?.tier && me?.tier_label === "Audit (one-time)";
  const initials = (me?.name || me?.email || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-bone flex" data-testid="portal-shell">
      <SEO noindex title="Client Portal — Beyond Valencia" canonical="/dashboard" />
      <aside className="hidden lg:flex flex-col w-64 border-r border-stone/40 bg-bone-dim min-h-screen sticky top-0">
        <Link to="/" className="px-6 pt-7 pb-5 border-b border-stone/30">
          <div className="font-serif text-xl text-ink tracking-tight leading-tight">Beyond<br />Valencia</div>
          <div className="text-[9px] uppercase tracking-[0.22em] text-brass mt-2">Client Portal</div>
        </Link>
        <nav className="flex-1 py-5 px-3 space-y-1">
          {NAV.map((n) => {
            if (n.paidOnly && !isPaid) return null;
            const Icon = n.icon;
            return (
              <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-ivy text-bone" : "text-ink-soft hover:bg-stone/10 hover:text-ink"}`} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <Icon className="h-4 w-4" /><span>{n.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-stone/30 p-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 bg-ivy text-bone flex items-center justify-center font-serif text-sm">{initials}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-ink truncate font-medium">{me?.name || "—"}</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-brass" data-testid="rail-tier-label">{me?.tier_label || "—"}</div>
            </div>
          </div>
          <button onClick={async () => { await logout(); navigate("/", { replace: true }); }} className="mt-4 flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-stone hover:text-ink"><LogOut className="h-3 w-3" /> Sign out</button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="border-b border-stone/30 bg-bone px-5 sm:px-8 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-baseline gap-3">
            <span className="text-[10px] uppercase tracking-[0.22em] text-stone">Portal</span>
            <ChevronRight className="h-3 w-3 text-stone" />
            <span className="text-[11px] uppercase tracking-[0.22em] text-ivy font-semibold">{location.pathname.split("/").pop()?.replace(/-/g, " ") || "dashboard"}</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-stone flex items-center gap-2">Tier: <span className="text-ink font-semibold tracking-tight" data-testid="header-tier">{me?.tier_label}</span></div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-stone flex items-center gap-2"><StatusDot level={me?.status_level} /> {me?.status_label}</div>
          </div>
        </div>

        {isAuditOnly && (
          <div className="bg-brass/10 border-b border-brass/40 px-5 sm:px-8 py-3 text-[12px] text-ink" data-testid="audit-only-banner">
            You purchased a one-time Transfer Audit. Document Vault, Roadmap edits, and Advisor notes are part of a recurring tier — <Link to="/pricing" className="text-ivy font-semibold underline">see ongoing engagement →</Link>
          </div>
        )}

        <div className="px-5 sm:px-8 py-8 lg:py-10 max-w-6xl">
          <Outlet context={{ me, isPaid, isAuditOnly }} />
        </div>
      </main>
    </div>
  );
}

// ----------------- Pages -----------------
import { useOutletContext } from "react-router-dom";
export const useMe = () => useOutletContext();

// === Dashboard ===
export function PortalDashboard() {
  const { me } = useMe();
  const [runs, setRuns] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [msForm, setMsForm] = useState({ title: "", when: "this_semester", notes: "" });
  const [msBusy, setMsBusy] = useState(false);
  const [msFlashId, setMsFlashId] = useState(null);

  useEffect(() => { (async () => {
    try { setRuns((await api.get("/portal/tool-runs?limit=3")).data || []); } catch {}
    try { setDeadlines((await api.get("/portal/deadlines")).data?.slice(0, 3) || []); } catch {}
    try {
      const r = await api.get("/portal/roadmap");
      const personal = (r.data || []).filter((it) => (it.source || "").startsWith("portal:") || it.source === "manual").slice(0, 5);
      setMilestones(personal);
    } catch { /* portal roadmap empty is fine */ }
    try { setAnalyses((await api.get("/mishap/my-analyses")).data || []); } catch { /* unauth or none */ }
  })(); }, []);

  const addMilestone = async (e) => {
    e.preventDefault();
    const title = msForm.title.trim();
    if (!title) { toast.error("Title required"); return; }
    if (title.length > 120) { toast.error("Max 120 chars"); return; }
    setMsBusy(true);
    try {
      const r = await api.post("/portal/milestones", { title, when: msForm.when, notes: msForm.notes || "" });
      const created = { ...r.data, item_id: r.data.item_id || `local_${Date.now()}` };
      setMilestones((prev) => [created, ...prev].slice(0, 8));
      setMsFlashId(created.item_id);
      setMsForm({ title: "", when: "this_semester", notes: "" });
      setTimeout(() => setMsFlashId(null), 2000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add milestone");
    } finally { setMsBusy(false); }
  };

  const progress = me?.roadmap_progress || { completed: 0, total: 0 };
  const pct = progress.total ? Math.round((progress.completed / progress.total) * 100) : 0;
  const firstName = (me?.name || "").split(" ")[0] || "there";
  const latestProb = runs.find((r) => r.tool === "probability-dashboard");
  const bucketLabel = (b) => ({ next_30_days: "Next 30 days", this_semester: "This semester", next_semester: "Next semester", before_transfer: "Before transfer" }[b] || b);

  return (
    <div className="space-y-10" data-testid="portal-dashboard">
      {me?.access_source === "granted" && (
        <div className="border border-ivy bg-ivy/5 p-4 flex items-baseline justify-between gap-3 flex-wrap" data-testid="dashboard-granted-badge">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-ivy font-semibold">Access granted by the Beyond Valencia team</div>
            <div className="text-xs text-ink-soft mt-1">You have complimentary access to your current tier. Enjoy.</div>
          </div>
        </div>
      )}
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Portal</div>
        <h1 className="font-serif font-medium text-4xl sm:text-5xl text-ink tracking-tightest mt-3">Welcome, {firstName}.</h1>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-stone/40">
        <div className="border-r border-b border-stone/40 bg-bone p-6"><div className="text-[10px] uppercase tracking-[0.22em] text-brass">Roadmap progress</div><div className="font-serif text-4xl text-ink mt-3 tabular-nums">{progress.completed}<span className="text-stone text-2xl"> / {progress.total}</span></div><div className="mt-3 h-[3px] bg-stone/30"><div className="h-full bg-ivy" style={{ width: `${pct}%` }} /></div></div>
        <div className="border-r border-b border-stone/40 bg-bone p-6"><div className="text-[10px] uppercase tracking-[0.22em] text-brass">Tier</div><div className="font-serif text-2xl text-ink mt-3">{me?.tier_label}</div>{!me?.tier && <Link to="/pricing" className="mt-2 inline-block text-[11px] uppercase tracking-[0.22em] text-ivy hover:text-brass">Choose a tier →</Link>}</div>
        <div className="border-r border-b border-stone/40 bg-bone p-6"><div className="text-[10px] uppercase tracking-[0.22em] text-brass">Status</div><div className="font-serif text-2xl text-ink mt-3 flex items-center gap-3"><StatusDot level={me?.status_level} /> {me?.status_label}</div></div>
      </div>

      {/* Personal milestones — inline add (Phase E.3) */}
      <section className="border border-stone/40 bg-bone p-6" data-testid="portal-milestones-section">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Personal milestones</div>
            <h2 className="font-serif text-2xl text-ink tracking-tight mt-1">Add a milestone</h2>
          </div>
          <Link to="/dashboard/roadmap" className="text-[11px] uppercase tracking-[0.22em] text-ivy hover:text-brass">See full roadmap →</Link>
        </div>
        <form onSubmit={addMilestone} className="mt-5 grid sm:grid-cols-12 gap-3" data-testid="milestone-inline-form">
          <input
            className="sm:col-span-5 bg-bone-dim border border-stone/40 px-3 py-2 text-sm text-ink placeholder:text-stone focus:outline-none focus:border-ivy"
            placeholder="Milestone (e.g. Meet Prof. Ortega)"
            maxLength={120}
            value={msForm.title}
            onChange={(e) => setMsForm({ ...msForm, title: e.target.value })}
            data-testid="milestone-title-input"
          />
          <select
            className="sm:col-span-3 bg-bone-dim border border-stone/40 px-3 py-2 text-sm text-ink focus:outline-none focus:border-ivy"
            value={msForm.when}
            onChange={(e) => setMsForm({ ...msForm, when: e.target.value })}
            data-testid="milestone-when-select"
          >
            <option value="next_30_days">Next 30 days</option>
            <option value="this_semester">This semester</option>
            <option value="next_semester">Next semester</option>
            <option value="before_transfer">Before transfer</option>
          </select>
          <input
            className="sm:col-span-3 bg-bone-dim border border-stone/40 px-3 py-2 text-sm text-ink placeholder:text-stone focus:outline-none focus:border-ivy"
            placeholder="Notes (optional)"
            value={msForm.notes}
            onChange={(e) => setMsForm({ ...msForm, notes: e.target.value })}
            data-testid="milestone-notes-input"
          />
          <button
            type="submit"
            disabled={msBusy || !msForm.title.trim()}
            className="sm:col-span-1 bg-ivy text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-2 px-3 disabled:opacity-50 hover:bg-ivy/90"
            data-testid="milestone-submit-btn"
          >{msBusy ? "…" : "Add"}</button>
        </form>
        <div className="mt-5">
          {milestones.length === 0 ? (
            <p className="text-sm text-ink-soft">No personal milestones yet. Add one above.</p>
          ) : (
            <ul className="border-t border-l border-stone/40" data-testid="milestones-list">
              {milestones.map((m) => (
                <li
                  key={m.item_id || m.milestone_label}
                  className={`border-r border-b border-stone/40 px-4 py-3 grid grid-cols-12 gap-2 items-baseline transition-colors ${msFlashId === m.item_id ? "bg-brass/15" : "bg-bone"}`}
                  data-testid={`milestone-row-${m.item_id}`}
                >
                  <div className="col-span-3 text-[10px] uppercase tracking-[0.18em] text-brass">{bucketLabel(m.when_bucket) || m.semester_label || "—"}</div>
                  <div className="col-span-6 text-sm text-ink">{m.milestone_label}{m.notes && <span className="block text-xs text-stone mt-1">{m.notes}</span>}</div>
                  <div className="col-span-3 text-right text-[10px] uppercase tracking-[0.18em] text-stone">{(m.created_at || "").slice(0, 10) || "just added"}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Phase H.1 — Your transfer plans / analyses */}
      <section className="border border-stone/40 bg-bone p-6" data-testid="portal-my-analyses-section">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Your transfer plans</div>
            <h2 className="font-serif text-2xl text-ink tracking-tight mt-1">Mishap analyses</h2>
          </div>
          <Link to="/mishap-corrector" className="text-[11px] uppercase tracking-[0.22em] text-ivy hover:text-brass">Run a new analysis →</Link>
        </div>
        {analyses.length === 0 ? (
          <div className="mt-5 border border-stone/30 bg-bone-dim/30 px-4 py-6 flex items-baseline justify-between flex-wrap gap-3" data-testid="portal-my-analyses-empty">
            <p className="text-sm text-ink-soft">No mishap analyses yet. Upload your transcript to see where your transfer path may need correction.</p>
            <Link to="/mishap-corrector" className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ivy hover:text-brass">Run Mishap Corrector →</Link>
          </div>
        ) : (
          <ul className="mt-5 border-t border-l border-stone/40" data-testid="portal-my-analyses-list">
            {analyses.slice(0, 6).map((a) => {
              const critCount = (a.mishaps || []).filter((m) => m.severity === "critical").length;
              const highCount = (a.mishaps || []).filter((m) => m.severity === "high").length;
              return (
                <li key={a.analysis_id} className="border-r border-b border-stone/40 bg-bone px-4 py-3 grid grid-cols-12 gap-2 items-baseline" data-testid={`portal-my-analysis-${a.analysis_id}`}>
                  <div className="col-span-4 text-sm text-ink">{a.program_name || a.program_slug}</div>
                  <div className="col-span-3 space-x-1">
                    {critCount > 0 && <span className="inline-block px-1.5 py-0.5 text-[10px] uppercase tracking-[0.14em] font-semibold bg-crimson text-bone">{critCount} crit</span>}
                    {highCount > 0 && <span className="inline-block px-1.5 py-0.5 text-[10px] uppercase tracking-[0.14em] font-semibold bg-brass text-ivy">{highCount} high</span>}
                    {critCount === 0 && highCount === 0 && <span className="text-[10px] uppercase tracking-[0.14em] text-stone">On track</span>}
                  </div>
                  <div className="col-span-2 text-[10px] uppercase tracking-[0.18em] text-stone tabular-nums">Extra sem {a.extra_semesters_added ?? 0}</div>
                  <div className="col-span-2 text-[10px] uppercase tracking-[0.18em] text-stone tabular-nums">{(a.generated_at || "").slice(0, 10)}</div>
                  <div className="col-span-1 text-right">
                    <Link to={`/mishap-corrector/results/${a.analysis_id}`} className="text-[11px] uppercase tracking-[0.18em] text-ivy hover:text-brass">Open →</Link>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <div className="grid lg:grid-cols-2 gap-0 border-t border-l border-stone/40">
        <div className="border-r border-b border-stone/40 bg-bone p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Next deadlines</div>
          {deadlines.length === 0 ? <p className="mt-4 text-sm text-ink-soft">No deadlines configured. <Link to="/dashboard/account" className="text-ivy font-semibold">Set target schools →</Link></p> : (
            <ul className="mt-4 space-y-3">{deadlines.map((d) => (<li key={d.deadline_id} className="flex items-baseline justify-between gap-3 border-b border-stone/20 pb-3"><span className="text-sm text-ink">{d.school} · {d.type}</span><span className="font-mono text-xs text-stone">{d.date}</span></li>))}</ul>
          )}
        </div>
        <div className="border-r border-b border-stone/40 bg-bone p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Recent tool runs</div>
          {runs.length === 0 ? <p className="mt-4 text-sm text-ink-soft">No tool runs yet. <Link to="/tools" className="text-ivy font-semibold">Run a tool →</Link></p> : (
            <ul className="mt-4 space-y-3">{runs.map((r) => (<li key={r.run_id} className="flex items-baseline justify-between gap-3 border-b border-stone/20 pb-3"><Link to={`/dashboard/tools`} className="text-sm text-ink hover:text-ivy">{r.tool}</Link><span className="font-mono text-xs text-stone">{(r.created_at || "").slice(0, 10)}</span></li>))}</ul>
          )}
        </div>
      </div>
      {latestProb && <div className="border border-stone/40 bg-ivy text-bone p-7"><div className="text-[10px] uppercase tracking-[0.22em] text-brass-soft">Suggested next action</div><p className="mt-3 font-serif text-xl tracking-tight">{latestProb.outputs?.recommended_next_step}</p></div>}
    </div>
  );
}

// === Tools history ===
export function PortalTools() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/portal/tool-runs?limit=50").then((r) => setRows(r.data || [])); }, []);
  return (
    <div className="space-y-6" data-testid="portal-tools">
      <h1 className="font-serif text-3xl text-ink tracking-tight">My Tool Runs</h1>
      {rows.length === 0 ? <p className="text-ink-soft">No tool runs yet. <Link to="/tools" className="text-ivy underline">Run a tool</Link>.</p> : (
        <div className="border-t border-l border-stone/40">
          {rows.map((r) => (
            <div key={r.run_id} className="border-r border-b border-stone/40 bg-bone p-5 grid grid-cols-12 gap-3 items-baseline" data-testid={`run-row-${r.run_id}`}>
              <div className="col-span-4 font-serif text-lg text-ink tracking-tight">{r.tool}</div>
              <div className="col-span-3 font-mono text-xs text-stone tabular-nums">{(r.created_at || "").slice(0, 10)}</div>
              <div className="col-span-3 text-sm text-ink-soft truncate">{r.outputs?.readiness_label || r.outputs?.applicant_type || r.outputs?.projected_final_gpa || "—"}</div>
              <div className="col-span-2 text-right text-[11px] uppercase tracking-[0.22em] text-stone">{r.outputs?.readiness_score ?? ""}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// === Transcripts ===
export function PortalTranscripts() {
  const [rows, setRows] = useState([]);
  const reload = () => api.get("/portal/transcript-audits").then((r) => setRows(r.data || []));
  useEffect(() => { reload(); }, []);
  const del = async (id) => { if (!window.confirm("Delete this audit?")) return; await api.delete(`/portal/transcript-audits/${id}`); reload(); };
  return (
    <div className="space-y-6" data-testid="portal-transcripts">
      <div className="flex items-baseline justify-between"><h1 className="font-serif text-3xl text-ink tracking-tight">Transcript Audits</h1><Link to="/tools/transcript-audit?from=portal" className="bg-ivy text-bone uppercase tracking-[0.18em] text-xs font-semibold py-3 px-5">New audit</Link></div>
      {rows.length === 0 ? <p className="text-ink-soft">No transcripts uploaded yet.</p> : (
        <div className="grid md:grid-cols-2 gap-0 border-t border-l border-stone/40">
          {rows.map((r) => (
            <div key={r.audit_id} className="border-r border-b border-stone/40 bg-bone p-6">
              <div className="text-[10px] uppercase tracking-[0.22em] text-brass">{r.created_at?.slice(0, 10)}</div>
              <div className="font-serif text-xl text-ink mt-2">{r.filename || r.audit_id}</div>
              <div className="text-sm text-stone mt-2">{r.outputs?.target_university_readiness?.school} · {r.outputs?.aa_completion?.status?.replace(/_/g, " ")}</div>
              <div className="mt-5 flex gap-3"><span className="text-[10px] uppercase tracking-[0.22em] text-ivy font-semibold">{r.outputs?.applicant_type || "—"}</span><button onClick={() => del(r.audit_id)} className="ml-auto text-stone hover:text-ink"><Trash2 className="h-4 w-4" /></button></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// === Deadlines ===
export function PortalDeadlines() {
  const { me } = useMe();
  const [rows, setRows] = useState([]);
  const [view, setView] = useState("list");
  useEffect(() => { api.get("/portal/deadlines").then((r) => setRows(r.data || [])); }, []);
  const dl = (d) => {
    const dt = (d.date || "").replace(/-/g, "");
    const ics = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//BV//EN", "BEGIN:VEVENT", `UID:${d.deadline_id}@bv`, `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, "").split(".")[0]}Z`, `DTSTART;VALUE=DATE:${dt}`, `SUMMARY:${d.school} — ${d.type}`, `DESCRIPTION:${(d.notes || "").replace(/\n/g, " ")}`, "END:VEVENT", "END:VCALENDAR"].join("\r\n");
    const blob = new Blob([ics], { type: "text/calendar" }); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `${d.school}_${d.date}.ics`; a.click(); URL.revokeObjectURL(url);
  };
  const apiUrl = process.env.REACT_APP_BACKEND_URL;
  const feed = me?.calendar_token ? `${apiUrl}/api/portal/deadlines/feed.ics?token=${me.calendar_token}` : null;
  return (
    <div className="space-y-6" data-testid="portal-deadlines">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <h1 className="font-serif text-3xl text-ink tracking-tight">Deadlines</h1>
        <div className="flex border border-stone/40" data-testid="deadlines-view-toggle">
          {[["list", "List"], ["calendar", "Calendar"]].map(([v, l]) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 ${view === v ? "bg-ivy text-bone" : "text-ink hover:bg-stone/10"}`}
              data-testid={`deadlines-view-${v}`}
            >{l}</button>
          ))}
        </div>
      </div>
      {feed && <div className="bg-bone-dim border border-stone/40 p-4 text-xs text-ink-soft">Subscribe (.ics feed): <a href={feed} className="text-ivy underline break-all">{feed}</a></div>}
      {rows.length === 0 ? <p className="text-ink-soft">No deadlines for your target schools. Set them in <Link to="/dashboard/account" className="text-ivy underline">Account</Link>.</p> : view === "calendar" ? (
        <DeadlineCalendar deadlines={rows} onDownload={dl} />
      ) : (
        <div className="border-t border-l border-stone/40">
          {rows.map((d) => (
            <div key={d.deadline_id} className="border-r border-b border-stone/40 bg-bone p-5 grid grid-cols-12 gap-3 items-baseline">
              <div className="col-span-2 text-[11px] uppercase tracking-[0.18em] text-brass font-semibold">{d.school}</div>
              <div className="col-span-2 text-[11px] uppercase tracking-[0.18em] text-stone">{d.type} · {d.cycle_label}</div>
              <div className="col-span-2 font-mono text-sm text-ink tabular-nums">{d.date}</div>
              <div className="col-span-5 text-sm text-ink-soft">{d.notes}</div>
              <button onClick={() => dl(d)} className="col-span-1 text-stone hover:text-ivy flex justify-end"><Download className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// === Vault ===
export function PortalVault() {
  const { isAuditOnly } = useMe();
  const [data, setData] = useState({ documents: [], total_used_bytes: 0, total_max_bytes: 0 });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const reload = () => api.get("/portal/vault").then((r) => setData(r.data));
  useEffect(() => { reload(); }, []);
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f); fd.append("category", "Misc");
      await api.post("/portal/vault/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded"); reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const dl = (d) => { window.open(`${process.env.REACT_APP_BACKEND_URL}/api/portal/vault/${d.document_id}`, "_blank"); };
  const rm = async (d) => { if (!window.confirm(`Delete ${d.original_filename}?`)) return; await api.delete(`/portal/vault/${d.document_id}`); reload(); };
  const used = Math.round(data.total_used_bytes / 1024 / 1024);
  const max = Math.round((data.total_max_bytes || 200 * 1024 * 1024) / 1024 / 1024);
  if (isAuditOnly) return <div className="border border-stone/40 bg-bone p-7 text-ink-soft" data-testid="portal-vault">Document Vault is available with a recurring tier. <Link to="/pricing" className="text-ivy underline">See ongoing engagement</Link>.</div>;
  return (
    <div className="space-y-6" data-testid="portal-vault">
      <div className="flex items-baseline justify-between flex-wrap gap-3"><h1 className="font-serif text-3xl text-ink tracking-tight">Document Vault</h1><div className="text-[11px] uppercase tracking-[0.18em] text-stone tabular-nums">{used} MB of {max} MB used</div></div>
      <div><input ref={fileRef} type="file" hidden onChange={onFile} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt" data-testid="vault-file-input" /><button disabled={uploading} onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 bg-ivy text-bone uppercase tracking-[0.18em] text-xs font-semibold py-3 px-5 disabled:opacity-60" data-testid="vault-upload-btn"><Upload className="h-3 w-3" /> {uploading ? "Uploading…" : "Upload document"}</button></div>
      {data.documents.length === 0 ? <p className="text-ink-soft">No documents yet.</p> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-stone/40">
          {data.documents.map((d) => (
            <div key={d.document_id} className="border-r border-b border-stone/40 bg-bone p-5">
              <div className="flex gap-4">
                <PdfThumbnail documentId={d.document_id} filename={d.original_filename} mimeType={d.mime_type} />
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-brass">{d.category}</div>
                  <div className="font-serif text-base text-ink mt-2 break-all">{d.original_filename}</div>
                  <div className="text-xs text-stone mt-1 tabular-nums">{Math.round(d.size_bytes / 1024)} KB · {d.uploaded_at?.slice(0, 10)}</div>
                </div>
              </div>
              <div className="mt-4 flex gap-3"><button onClick={() => dl(d)} className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ivy hover:text-brass inline-flex items-center gap-1"><Download className="h-3 w-3" /> Download</button><button onClick={() => rm(d)} className="ml-auto text-stone hover:text-ink"><Trash2 className="h-4 w-4" /></button></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// === Roadmap ===
export function PortalRoadmap() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const reload = async () => { const r = await api.get("/portal/roadmap"); setItems(r.data || []); };
  useEffect(() => { (async () => {
    try { await reload(); const r2 = await api.get("/portal/roadmap"); if ((r2.data || []).length === 0) { await api.post("/portal/roadmap/generate"); await reload(); } } catch {}
    finally { setLoading(false); }
  })(); }, []);
  const toggle = async (it) => {
    const next = it.status === "done" ? "open" : "done";
    await api.patch(`/portal/roadmap/items/${it.item_id}`, { milestone_label: it.milestone_label, status: next });
    reload();
  };
  if (loading) return <p className="text-ink-soft" data-testid="portal-roadmap">Generating roadmap…</p>;
  return (
    <div className="space-y-6" data-testid="portal-roadmap">
      <h1 className="font-serif text-3xl text-ink tracking-tight">Roadmap</h1>
      <div className="border-t border-l border-stone/40">
        {items.map((it) => (
          <div key={it.item_id} className="border-r border-b border-stone/40 bg-bone p-5 grid grid-cols-12 gap-3 items-baseline">
            <div className="col-span-2 text-[11px] uppercase tracking-[0.18em] text-brass">{it.semester_label}</div>
            <div className="col-span-7"><div className={`font-serif text-lg tracking-tight ${it.status === "done" ? "line-through text-stone" : "text-ink"}`}>{it.milestone_label}</div>{it.notes && <div className="text-xs text-stone mt-1">{it.notes}</div>}</div>
            <div className="col-span-2 text-xs text-stone">{it.due_date || ""}</div>
            <button onClick={() => toggle(it)} className="col-span-1 text-[10px] uppercase tracking-[0.18em] font-semibold text-ivy hover:text-brass text-right">{it.status === "done" ? "Undo" : "Mark done"}</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// === Advisor ===
export function PortalAdvisor() {
  const { isPaid } = useMe();
  const [notes, setNotes] = useState([]);
  const [meetings, setMeetings] = useState([]);
  useEffect(() => { if (!isPaid) return;
    api.get("/portal/advisor/notes").then((r) => setNotes(r.data || [])).catch(() => {});
    api.get("/portal/advisor/meetings").then((r) => setMeetings(r.data || [])).catch(() => {});
  }, [isPaid]);
  if (!isPaid) return (
    <div className="border border-stone/40 bg-bone p-8" data-testid="portal-advisor">
      <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Locked</div>
      <h2 className="font-serif text-2xl text-ink tracking-tight mt-2">Advisor notes &amp; meetings</h2>
      <p className="text-sm text-ink-soft mt-3">Available with Florida Transfer or Selective &amp; Elite Transfer.</p>
      <Link to="/pricing" className="mt-5 inline-block bg-ivy text-bone uppercase tracking-[0.18em] text-xs font-semibold py-3 px-5">See ongoing engagement →</Link>
    </div>
  );
  return (
    <div className="space-y-10" data-testid="portal-advisor">
      <h1 className="font-serif text-3xl text-ink tracking-tight">Advisor</h1>
      <section>
        <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Notes</div>
        {notes.length === 0 ? <p className="text-ink-soft mt-3">No notes yet.</p> : (
          <div className="mt-5 space-y-5">{notes.map((n) => (
            <article key={n.note_id} className="border border-stone/40 bg-bone p-6">
              <div className="text-[10px] uppercase tracking-[0.22em] text-stone tabular-nums">{n.created_at?.slice(0, 10)}</div>
              <h3 className="font-serif text-xl text-ink mt-2 tracking-tight">{n.title}</h3>
              <div className="mt-4 text-sm text-ink-soft whitespace-pre-wrap leading-relaxed">{n.body_markdown}</div>
            </article>))}</div>
        )}
      </section>
      <section>
        <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Meetings</div>
        {meetings.length === 0 ? <p className="text-ink-soft mt-3">No meetings logged.</p> : (
          <div className="mt-5 border-t border-l border-stone/40">{meetings.map((m) => (
            <div key={m.meeting_id} className="border-r border-b border-stone/40 bg-bone p-5 grid grid-cols-12 gap-3 items-baseline">
              <div className="col-span-2 font-mono text-sm text-ink tabular-nums">{m.date?.slice(0, 10)}</div>
              <div className="col-span-1 text-xs text-stone">{m.duration_minutes}m</div>
              <div className="col-span-9 text-sm text-ink-soft">{m.summary}{m.action_items?.length > 0 && <ul className="mt-2 list-disc pl-5 text-xs">{m.action_items.map((a, i) => <li key={i}>{a}</li>)}</ul>}</div>
            </div>))}</div>
        )}
      </section>
    </div>
  );
}

// === Account ===
export function PortalAccount() {
  const { me } = useMe();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (me) setForm({ target_schools: (me.target_schools || []).join(", "), intended_major: me.intended_major || "", current_college: me.current_college || "", aa_status: me.aa_status || "in_progress", email_lead_acks: me.email_lead_acks, email_nurture: me.email_nurture }); }, [me]);
  if (!form) return null;
  const save = async (e) => { e.preventDefault(); setBusy(true);
    try {
      await api.patch("/portal/me", { ...form, target_schools: form.target_schools.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Saved");
    } catch (err) { toast.error("Save failed"); } finally { setBusy(false); }
  };
  const regen = async () => { const r = await api.post("/portal/calendar-token/regenerate"); toast.success("New token generated"); window.location.reload(); };
  return (
    <div className="space-y-8 max-w-3xl" data-testid="portal-account">
      <h1 className="font-serif text-3xl text-ink tracking-tight">Account</h1>
      <div className="border border-stone/40 bg-bone-dim p-5">
        <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Tier</div>
        <div className="font-serif text-2xl text-ink mt-2" data-testid="account-tier-pill">{me.tier_label}</div>
        {!me.tier && <Link to="/pricing" className="mt-3 inline-block text-[11px] uppercase tracking-[0.22em] font-semibold text-ivy hover:text-brass">Upgrade →</Link>}
      </div>
      <form onSubmit={save} className="space-y-5">
        <div><label className="text-[11px] uppercase tracking-[0.22em] text-stone">Target schools (comma-separated)</label><input className="mt-2 w-full bg-bone border border-stone/40 px-3 py-2 text-sm" value={form.target_schools} onChange={(e) => setForm({ ...form, target_schools: e.target.value })} placeholder="UF, UCF, Harvard" /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="text-[11px] uppercase tracking-[0.22em] text-stone">Intended major</label><input className="mt-2 w-full bg-bone border border-stone/40 px-3 py-2 text-sm" value={form.intended_major} onChange={(e) => setForm({ ...form, intended_major: e.target.value })} /></div>
          <div><label className="text-[11px] uppercase tracking-[0.22em] text-stone">Current college</label><input className="mt-2 w-full bg-bone border border-stone/40 px-3 py-2 text-sm" value={form.current_college} onChange={(e) => setForm({ ...form, current_college: e.target.value })} /></div>
          <div><label className="text-[11px] uppercase tracking-[0.22em] text-stone">AA status</label><select className="mt-2 w-full bg-bone border border-stone/40 px-3 py-2 text-sm" value={form.aa_status} onChange={(e) => setForm({ ...form, aa_status: e.target.value })}><option value="not_started">Not started</option><option value="in_progress">In progress</option><option value="completed">Completed</option></select></div>
        </div>
        <div className="flex gap-5"><label className="flex items-center gap-2 text-sm text-ink-soft"><input type="checkbox" checked={!!form.email_lead_acks} onChange={(e) => setForm({ ...form, email_lead_acks: e.target.checked })} /> Lead acknowledgements</label><label className="flex items-center gap-2 text-sm text-ink-soft"><input type="checkbox" checked={!!form.email_nurture} onChange={(e) => setForm({ ...form, email_nurture: e.target.checked })} /> Nurture series</label></div>
        <button type="submit" disabled={busy} className="bg-ivy text-bone uppercase tracking-[0.18em] text-xs font-semibold py-3 px-6" data-testid="account-save-btn">{busy ? "Saving…" : "Save"}</button>
      </form>
      <div className="border border-stone/40 bg-bone p-5">
        <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Calendar feed token</div>
        <div className="mt-3 flex items-baseline gap-3"><code className="text-xs bg-stone/10 px-2 py-1 break-all">{me.calendar_token || "(none yet — click regenerate)"}</code><button onClick={regen} className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ivy hover:text-brass">Regenerate</button></div>
      </div>

      <ReferralsSection />
    </div>
  );
}

// === Referrals (Phase 4.6) ===
function ReferralsSection() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try { const r = await api.get("/portal/referrals/summary"); setData(r.data); }
    catch { toast.error("Could not load referrals"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
    catch { toast.error("Copy failed — select & copy manually"); }
  };

  const mailtoHref = () => {
    if (!data?.share_url) return "#";
    const subject = "Beyond Valencia — $50 off your Transfer Audit";
    const body =
`Hey —

I've been working with Beyond Valencia (Taimur Ahmed, Harvard → ex-Valencia → UF) on transfer admissions strategy. They run a $249 Transfer Audit that reviews your transcript against your target school's actual prerequisites and gives you a semester-by-semester plan.

If you use my code you get $50 off. Here's the link:

${data.share_url}

Take a look — it's the most useful $199 I've ever seen for a transfer student.`;
    return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  if (busy && !data) return <div className="text-sm text-ink-soft" data-testid="referrals-loading">Loading referrals…</div>;
  if (!data) return null;

  return (
    <section className="border border-stone/40 bg-bone-dim p-6 lg:p-7" data-testid="referrals-section">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Refer a friend</div>
          <h2 className="font-serif text-2xl text-ink tracking-tight mt-2">$50 for them. $50 for you.</h2>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.22em] text-stone">Your credit balance</div>
          <div className="font-serif text-3xl text-ivy tabular-nums" data-testid="referrals-credit-balance">{data.credit_balance_display}</div>
        </div>
      </div>

      <p className="mt-4 text-sm text-ink-soft leading-relaxed max-w-xl">
        Share your code or link. When a friend buys the Transfer Audit, they get $50 off — and you get $50 in credit toward Florida Transfer or Selective &amp; Elite.
      </p>

      {/* Code */}
      <div className="mt-6 grid sm:grid-cols-12 gap-3">
        <div className="sm:col-span-5">
          <label className="text-[11px] uppercase tracking-[0.22em] text-stone">Your code</label>
          <div className="mt-2 flex items-stretch border border-stone/40 bg-bone">
            <code className="flex-1 font-mono text-base text-ink px-3 py-2 tracking-wider" data-testid="referrals-code">{data.referral_code}</code>
            <button onClick={() => copy(data.referral_code, "Code")} aria-label="Copy code" className="border-l border-stone/40 px-3 text-ink-soft hover:bg-ivy hover:text-bone transition-colors" data-testid="referrals-copy-code">
              <CopyIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="sm:col-span-7">
          <label className="text-[11px] uppercase tracking-[0.22em] text-stone">Share link</label>
          <div className="mt-2 flex items-stretch border border-stone/40 bg-bone">
            <code className="flex-1 font-mono text-xs text-ink-soft px-3 py-2 truncate" data-testid="referrals-share-url">{data.share_url}</code>
            <button onClick={() => copy(data.share_url, "Link")} aria-label="Copy link" className="border-l border-stone/40 px-3 text-ink-soft hover:bg-ivy hover:text-bone transition-colors" data-testid="referrals-copy-url">
              <CopyIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Share via email — outline secondary, brass on bone */}
      <a
        href={mailtoHref()}
        className="mt-5 inline-flex items-center gap-2 border border-brass text-brass hover:bg-brass hover:text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-3 px-5 transition-colors"
        data-testid="referrals-share-email"
      >
        <Mail className="h-3.5 w-3.5" /> Share via email
      </a>

      {/* Stats */}
      <div className="mt-7 grid grid-cols-3 border-t border-l border-stone/40">
        <div className="border-r border-b border-stone/40 bg-bone p-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-stone">Total referred</div>
          <div className="font-serif text-2xl text-ink mt-1 tabular-nums" data-testid="referrals-stat-total">{data.total_referred}</div>
        </div>
        <div className="border-r border-b border-stone/40 bg-bone p-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-stone">Credited</div>
          <div className="font-serif text-2xl text-ivy mt-1 tabular-nums" data-testid="referrals-stat-credited">{data.total_credited}</div>
        </div>
        <div className="border-r border-b border-stone/40 bg-bone p-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-stone">Pending</div>
          <div className="font-serif text-2xl text-brass mt-1 tabular-nums" data-testid="referrals-stat-pending">{data.total_pending}</div>
        </div>
      </div>

      {/* Recent referrals table */}
      <div className="mt-6">
        <div className="text-[11px] uppercase tracking-[0.22em] text-stone">Recent referrals</div>
        {data.recent_referrals.length === 0 ? (
          <p className="text-sm text-ink-soft mt-3" data-testid="referrals-empty">No referrals yet — share your code to get started.</p>
        ) : (
          <div className="mt-3 border-t border-l border-stone/40" data-testid="referrals-table">
            {data.recent_referrals.map((r, i) => (
              <div key={i} className="border-r border-b border-stone/40 bg-bone grid grid-cols-12 gap-3 items-baseline px-4 py-3">
                <div className="col-span-7 text-sm font-mono text-ink-soft truncate">{r.referee_email_masked || "—"}</div>
                <div className="col-span-3 text-[11px] uppercase tracking-[0.18em]">
                  <span className={r.status === "credited" ? "text-ivy" : "text-brass"}>{r.status}</span>
                </div>
                <div className="col-span-2 font-mono text-[11px] text-stone tabular-nums text-right">{(r.created_at || "").slice(0, 10)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
